import { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import { Button, FormControl, FormLabel, Grid, Input, Select, VStack } from '@chakra-ui/react'
import { UiSelect } from 'components/common'
import { getSalesUsers } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { ROLES } from 'lib'

export const CustomerFormType = {
  Add: 'add',
  Edit: 'edit'
}

export const CustomerForm = ({ type, onSubmit, isSubmitting, initialForm = null }) => {
  const { createToast, currentUser } = useAppContext()
  const [form, setForm] = useImmer({
    company: '',
    prefix: '',
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    salesUser: null,
    address: '',
    city: '',
    state: '',
    zip: ''
  })

  useEffect(() => {
    if (currentUser?.id && currentUser?.role === ROLES.SalesUser) {
      setForm(draft => { draft.salesUser = { ...currentUser } })
    }
  }, [currentUser])

  const buttonText = useMemo(() => {
    switch (type) {
      case CustomerFormType.Add:
        return 'Add Customer'
      case CustomerFormType.Edit:
        return 'Save Customer'
    }
  }, [type])

  useEffect(() => {
    setForm(draft => {
      Object.keys(initialForm || {}).forEach(k => { draft[k] = initialForm[k] ?? '' })
    })
  }, [initialForm])

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (form.salesUser?.id) {
      onSubmit(form)
    } else {
      createToast('error', 'Error', 'Select contact owner.')
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid templateColumns="repeat(2, 1fr)">
        <VStack gap={2} maxW="500px">
          <FormControl isRequired>
            <FormLabel>Company</FormLabel>
            <Input name="company" value={form.company} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Prefix</FormLabel>
            <Select name="prefix" value={form.prefix} onChange={handleChange} placeholder="Select">
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>First name</FormLabel>
            <Input name="firstname" value={form.firstname} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Last name</FormLabel>
            <Input name="lastname" value={form.lastname} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Email</FormLabel>
            <Input type="email" name="email" value={form.email} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Phone</FormLabel>
            <Input name="phone" value={form.phone} onChange={handleChange} />
          </FormControl>
        </VStack>
        <VStack gap={2} maxW="500px">
          <FormControl isRequired>
            <FormLabel>Contact Owner</FormLabel>
            <UiSelect
              value={form.salesUser}
              onChange={v => {
                setForm(draft => { draft.salesUser = v })
              }}
              loadData={(page, search) => getSalesUsers(page, 20, search, 'name', 'asc')}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => v.name}
              placeholder="Search Users"
              noOptionsMessage={() => 'No Users Found'}
              required={true}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Address</FormLabel>
            <Input name="address" value={form.address} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>City</FormLabel>
            <Input name="city" value={form.city} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>State</FormLabel>
            <Input name="state" value={form.state} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Zip</FormLabel>
            <Input name="zip" value={form.zip} onChange={handleChange} />
          </FormControl>
        </VStack>
      </Grid>
      <Button type="submit" mt={8} colorScheme="blue" isLoading={isSubmitting}>{buttonText}</Button>
    </form>
  )
}

CustomerForm.propTypes = {
  type: PropTypes.oneOf(Object.values(CustomerFormType)).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  initialForm: PropTypes.object
}
